import type { TaskBucket, VisibilityScope } from "@/services/api/agentic-v2";
import type { AppLocationCodec } from "@/navigation/appLocation";
import { NAV_KEYS } from "@/navigation/keys";
import { stripTransientUrlParams, TRANSIENT_URL_PARAM_KEYS } from "@/navigation/urlTransient";
import {
  isMissionListFocusKey,
  MISSION_LIST_FOCUS_VISIBILITY,
  parseMissionControlTaskBucket,
  parseMissionControlVisibility,
} from "@/components/mission-control/missionListFocus";

export type MissionControlView = "feed" | "tasks" | "missions" | "pipeline" | "artifacts";

const TOP_LEVEL_VIEWS = new Set<MissionControlView>([
  "feed",
  "tasks",
  "missions",
  "pipeline",
  "artifacts",
]);

export type MissionControlLocation = {
  view: MissionControlView;
  visibility: VisibilityScope;
  taskBucket?: TaskBucket;
  taskId?: string;
  focus?: string;
  asUserId?: string;
};

export function defaults(): MissionControlLocation {
  return { view: "feed", visibility: "team" };
}

function parseView(raw: string | null | undefined): MissionControlView | null {
  if (raw && TOP_LEVEL_VIEWS.has(raw as MissionControlView)) {
    return raw as MissionControlView;
  }
  return null;
}

function readString(params: Record<string, string | undefined>, key: string): string | undefined {
  return params[key]?.trim() || undefined;
}

export function parseMissionControlViewFromPath(pathname: string): MissionControlView | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "mission-control") return null;
  const segment = segments[1];
  if (!segment) return null;
  if (segment === "feed") return "feed";
  if (segment === "tasks") return "tasks";
  if (TOP_LEVEL_VIEWS.has(segment as MissionControlView)) {
    return segment as MissionControlView;
  }
  return null;
}

/** Path for a Mission Control tab (no open task thread). */
export function missionControlViewPath(view: MissionControlView): string {
  if (view === "feed") return "/mission-control/feed";
  return `/mission-control/${view}`;
}

/** Task thread path — tab is part of the URL (feed vs tasks), not inferred from `/tasks/:id` alone. */
export function missionControlTaskThreadPath(
  taskId: string,
  view: MissionControlView,
): string {
  const encoded = encodeURIComponent(taskId);
  if (view === "feed") {
    return `/mission-control/feed/tasks/${encoded}`;
  }
  return `/mission-control/tasks/${encoded}`;
}

export function normalize(loc: MissionControlLocation): MissionControlLocation {
  const next: MissionControlLocation = {
    view: loc.view,
    visibility: loc.visibility,
  };
  if (loc.taskBucket && loc.view === "tasks") next.taskBucket = loc.taskBucket;
  const taskThreadView = loc.view === "tasks" || loc.view === "feed";
  if (loc.taskId && (taskThreadView || loc.focus)) next.taskId = loc.taskId;
  if (loc.focus) next.focus = loc.focus;
  if (loc.asUserId) next.asUserId = loc.asUserId;

  if (isMissionListFocusKey(next.focus) || next.focus?.startsWith("task:")) {
    next.visibility = MISSION_LIST_FOCUS_VISIBILITY;
    if (!parseView(next.view)) {
      next.view = isMissionListFocusKey(next.focus) ? "missions" : "tasks";
    }
    if (next.focus?.startsWith("task:") && !next.taskBucket) {
      next.taskBucket = "all";
    }
  }

  return next;
}

function fromParams(params: Record<string, string | undefined>): MissionControlLocation {
  const base = defaults();
  const view = parseView(params[NAV_KEYS.view]);
  const visibility = parseMissionControlVisibility(params[NAV_KEYS.visibility]);
  const taskBucket = parseMissionControlTaskBucket(params[NAV_KEYS.taskBucket]);
  if (view) base.view = view;
  if (visibility) base.visibility = visibility;
  if (taskBucket) base.taskBucket = taskBucket;
  const taskId = readString(params, NAV_KEYS.taskId) ?? readString(params, NAV_KEYS.taskLegacy);
  if (taskId) base.taskId = taskId;
  const focus = readString(params, NAV_KEYS.focus);
  if (focus) base.focus = focus;
  const asUserId = readString(params, NAV_KEYS.asUserId);
  if (asUserId) base.asUserId = asUserId;

  if (!view && focus?.startsWith("task:")) base.view = "tasks";
  if (!view && isMissionListFocusKey(focus)) base.view = "missions";

  return normalize(base);
}

function fromUrl(pathname: string, searchParams: URLSearchParams): MissionControlLocation {
  const params: Record<string, string | undefined> = Object.fromEntries(searchParams);
  for (const key of TRANSIENT_URL_PARAM_KEYS) {
    delete params[key];
  }
  const pathView = parseMissionControlViewFromPath(pathname);
  const pathTaskId = parseMissionControlTaskIdFromPath(pathname);
  if (pathView) params[NAV_KEYS.view] = pathView;
  else if (pathTaskId) params[NAV_KEYS.view] = "tasks";
  if (pathTaskId) params[NAV_KEYS.taskId] = pathTaskId;
  return fromParams(params);
}

function toParams(loc: MissionControlLocation): Record<string, string> {
  const n = normalize(loc);
  const d = defaults();
  const out: Record<string, string> = {};
  if (n.view !== d.view) out[NAV_KEYS.view] = n.view;
  if (n.visibility !== d.visibility) out[NAV_KEYS.visibility] = n.visibility;
  if (n.taskBucket) out[NAV_KEYS.taskBucket] = n.taskBucket;
  if (n.taskId) out[NAV_KEYS.taskId] = n.taskId;
  if (n.focus) out[NAV_KEYS.focus] = n.focus;
  if (n.asUserId) out[NAV_KEYS.asUserId] = n.asUserId;
  return out;
}

function toUrl(loc: MissionControlLocation): string {
  const n = normalize(loc);
  const { [NAV_KEYS.view]: _view, ...query } = stripTransientUrlParams(toParams(n));
  const threadOpen = Boolean(n.taskId) && !n.focus;
  if (threadOpen) delete query[NAV_KEYS.taskId];
  const pathname = threadOpen ? missionControlTaskThreadPath(n.taskId!, n.view) : missionControlViewPath(n.view);
  const qs = new URLSearchParams(query).toString();
  return `${pathname}${qs ? `?${qs}` : ""}`;
}

export const missionControlCodec: AppLocationCodec<MissionControlLocation> = {
  appId: "mission-control",
  fromUrl,
  fromParams,
  toParams,
  toUrl,
};

/** Canonical path for Mission Control task thread on the Tasks tab (deep links / agent detail). */
export function missionControlTaskDetailPath(taskId: string): string {
  return missionControlTaskThreadPath(taskId, "tasks");
}

/** Parse task id from canonical or legacy paths (not approval/signoff routes). */
export function parseMissionControlTaskIdFromPath(pathname: string): string | undefined {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] === "mission-control") {
    if (segments[1] === "feed" && segments[2] === "tasks" && segments[3]) {
      return decodeURIComponent(segments[3]);
    }
    if (segments[1] === "tasks" && segments[2]) {
      return decodeURIComponent(segments[2]);
    }
    return undefined;
  }
  if (
    segments[0] === "tasks" &&
    segments[1] &&
    segments[2] !== "approval" &&
    segments[2] !== "close-signoff" &&
    !segments[2]
  ) {
    return decodeURIComponent(segments[1]);
  }
  return undefined;
}

/** Window params for mission/task list-focus navigation (agent catalog). */
export function missionListFocusWindowParams(focusKey: string): Record<string, string> {
  const loc = normalize({
    ...defaults(),
    view: focusKey.startsWith("task:") ? "tasks" : "missions",
    focus: focusKey,
    visibility: MISSION_LIST_FOCUS_VISIBILITY,
    ...(focusKey.startsWith("task:") ? { taskBucket: "all" as TaskBucket } : {}),
  });
  return toParams(loc);
}
